import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const containerStyle = { maxWidth: 1100, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, marginBottom: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#007BFF', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnDanger = { padding: '10px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnFilter = { padding: '8px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 };

const getEmptyForm = () => ({
  id: null, level: '', chapter: '', chapter_name: '', concept: '', concept_id: '', concept_type: '', difficulty: '',
  question_text: '', image_url: '',
  question_tag_1: '', question_tag_2: '', question_tag_3: '',
  answer_a: '', answer_a_tag1: '', answer_a_tag2: '', answer_a_tag3: '',
  answer_b: '', answer_b_tag1: '', answer_b_tag2: '', answer_b_tag3: '',
  answer_c: '', answer_c_tag1: '', answer_c_tag2: '', answer_c_tag3: '',
  answer_d: '', answer_d_tag1: '', answer_d_tag2: '', answer_d_tag3: '',
  correct_answer: 'A'
});

const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/["']/g, '').replace(/\/+$/, '');

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const baseUrl = API_BASE.replace(/\/api\/?$/, '');
  return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
};

// ====================
// ✅ Enhanced KaTeX Math Rendering Component
// ====================
const SUP_MAP = { '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9' };
const SUB_MAP = { '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4', '₅': '5', '₆': '6', '₇': '7', '₈': '8', '₉': '9' };

// Updated MASTER regex to handle complex fractions like "x^3 ln x / 3"
const MASTER = new RegExp(
  '(?<blank>_{3,})' +
  '|(?<intunicode>∫[₀-₉]+[⁰-⁹]+)' +
  '|(?<intlim>∫\\s*(?<ilo>\\d+)\\s*\\^\\s*(?<ihi>\\d+))' +
  '|(?<frac>(?<num>(?:\\([^)]*\\)|\\[[^\\]]*\\]|[^/+=\\-])+?)\\s*\\/\\s*(?<den>(?:\\([^)]*\\)|\\[[^\\]]*\\]|[^/+=\\-])+?))(?=\\s*(?:[+\\-=]|$|\\u221A|∫))' +
  '|(?<intg>∫)' +
  '|(?<sup>[A-Za-z0-9]+\\s*\\^\\s*(?:\\{[^}]+\\}|\\([^)]+\\)|[A-Za-z0-9\\-]+))',
  'g'
);

function toLatexMath(s) {
  let out = String(s);
  
  // 1. Handle exponents: e^(-x) -> e^{-x}, x^{2} -> x^{2}, y^-3 -> y^{-3}
  out = out.replace(/\^\s*(?:\{([^}]+)\}|\(([^)]+)\)|([A-Za-z0-9\-]+))/g, (match, p1, p2, p3) => {
    const exp = p1 || p2 || p3;
    return `^{${exp}}`;
  });
  
  // 2. Handle unicode superscripts (e.g., x²)
  out = out.replace(/[⁰-⁹]+/g, (mm) => '^{' + [...mm].map((c) => SUP_MAP[c] || c).join('') + '}');
  
  // 3. Handle unicode subscripts for integrals (e.g., ₀¹)
  out = out.replace(/([₀-₉]+)([⁰-⁹]+)/g, (match, sub, sup) => {
    const lower = [...sub].map(c => SUB_MAP[c] || c).join('');
    const upper = [...sup].map(c => SUP_MAP[c] || c).join('');
    return `\\int_{${lower}}^{${upper}}`;
  });
  
  // 4. Math function names
  out = out.replace(/\b(ln|log|sin|cos|tan|cot|sec|csc|sqrt)\b/g, '\\$1');
  
  // 5. Common symbols (Using Unicode escapes \u221A and \u221E to prevent terminal copy-paste corruption)
  out = out
    .replace(/±/g, '\\pm ')
    .replace(/×/g, '\\times ')
    .replace(/÷/g, '\\div ')
    .replace(/≤/g, '\\leq ')
    .replace(/≥/g, '\\geq ')
    .replace(/≠/g, '\\neq ')
    .replace(/→/g, '\\to ')
    .replace(/π/g, '\\pi ')
    .replace(/°/g, '^\\circ ')
    .replace(/\u221A/g, '\\sqrt ')   // Safe replacement for √
    .replace(/\u221E/g, '\\infty '); // Safe replacement for ∞
    
  return out;
}

function stripOuter(s) {
  const t = String(s).trim();
  if ((t.startsWith('(') && t.endsWith(')')) || (t.startsWith('[') && t.endsWith(']'))) {
    return t.slice(1, -1);
  }
  return t;
}

function MathText({ children }) {
  const raw = children == null ? '' : String(children);
  const out = [];
  let last = 0;
  let key = 0;

  for (const m of raw.matchAll(MASTER)) {
    if (m.index > last) out.push(<span key={key++}>{raw.slice(last, m.index)}</span>);

    const g = m.groups || {};
    let latex = null;

    if (g.blank) {
      latex = '\\underline{\\hspace{1.2cm}}';
    } else if (g.intunicode) {
      latex = toLatexMath(g.intunicode);
    } else if (g.intlim) {
      latex = `\\int_{${g.ilo}}^{${g.ihi}}`;
    } else if (g.frac) {
      const num = g.num || '';
      const den = g.den || '';
      // Guard: only treat as a fraction if it looks mathematical
      const looksMath = /\d/.test(num + den) || (num.length <= 15 && den.length <= 15);
      if (looksMath) {
        latex = `\\frac{${toLatexMath(stripOuter(num))}}{${toLatexMath(stripOuter(den))}}`;
      }
    } else if (g.intg) {
      latex = '\\int';
    } else if (g.sup) {
      latex = toLatexMath(g.sup);
    }

    if (latex) {
      out.push(
        <span
          key={key++}
          style={{ display: 'inline-block', margin: '0 2px', verticalAlign: 'middle' }}
          dangerouslySetInnerHTML={{ __html: katex.renderToString(latex, { throwOnError: false, strict: 'warn' }) }}
        />
      );
    } else {
      out.push(<span key={key++}>{m[0]}</span>);
    }

    last = m.index + m[0].length;
  }
  if (last < raw.length) out.push(<span key={key++}>{raw.slice(last)}</span>);

  return (
    <span style={{ fontFamily: 'inherit', fontSize: '1.05em', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{out}</span>
  );
}

export default function AdminMathView() {
  const navigate = useNavigate();
  
  const [allQuestions, setAllQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  
  const [levelFilter, setLevelFilter] = useState('');
  const [chapterFilter, setChapterFilter] = useState('');
  const [conceptFilter, setConceptFilter] = useState('');
  const [conceptTypeFilter, setConceptTypeFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');

  // ✅ Pagination state set to 25 to prevent UI hanging on large datasets
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState(getEmptyForm());
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => { fetchQuestions(); }, []);

  const fetchQuestions = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/questions/math`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setAllQuestions(data);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const availableChapters = useMemo(() => {
    const source = levelFilter ? allQuestions.filter(q => String(q.level) === String(levelFilter)) : allQuestions;
    return [...new Set(source.map(q => q.chapter_name || q.chapter).filter(Boolean))].sort();
  }, [allQuestions, levelFilter]);

  const availableConcepts = useMemo(() => {
    let source = allQuestions;
    if (levelFilter) source = source.filter(q => String(q.level) === String(levelFilter));
    if (chapterFilter) source = source.filter(q => (q.chapter_name || q.chapter) === chapterFilter);
    return [...new Set(source.map(q => q.concept).filter(Boolean))].sort();
  }, [allQuestions, levelFilter, chapterFilter]);

  useEffect(() => {
    let result = allQuestions;
    if (levelFilter) result = result.filter(q => String(q.level) === String(levelFilter));
    if (chapterFilter) result = result.filter(q => (q.chapter_name || q.chapter) === chapterFilter);
    if (conceptFilter) result = result.filter(q => q.concept === conceptFilter);
    if (conceptTypeFilter) result = result.filter(q => q.concept_type === conceptTypeFilter);
    if (difficultyFilter) result = result.filter(q => q.difficulty === difficultyFilter);
    setFilteredQuestions(result);
    setCurrentPage(1); // Reset to page 1 whenever filters change
  }, [allQuestions, levelFilter, chapterFilter, conceptFilter, conceptTypeFilter, difficultyFilter]);

  const handleLevelChange = (e) => {
    setLevelFilter(e.target.value);
    setChapterFilter('');
    setConceptFilter('');
  };

  const handleChapterChange = (e) => {
    setChapterFilter(e.target.value);
    setConceptFilter('');
  };

  const clearAllFilters = () => {
    setLevelFilter('');
    setChapterFilter('');
    setConceptFilter('');
    setConceptTypeFilter('');
    setDifficultyFilter('');
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleEditImageUpload = async (file) => {
    if (!file || !editingId) return;
    setUploadingImage(true);
    setError('');
    const fd = new FormData();
    fd.append('imageFile', file);
    fd.append('questionId', editingId);
    try {
      const res = await fetch(`${API_BASE}/admin/upload/math-question-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setFormData(prev => ({ ...prev, image_url: data.imageUrl }));
    } catch (err) {
      setError('Image upload failed: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => setFormData(prev => ({ ...prev, image_url: '' }));

  const handleSave = async () => {
    setError('');
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId ? `${API_BASE}/admin/questions/math/${editingId}` : `${API_BASE}/admin/questions/math`;
    try {
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Save failed');
      await fetchQuestions(); 
      setEditingId(null); 
      setShowAddForm(false); 
      setFormData(getEmptyForm());
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archive this question?')) return;
    try {
      const res = await fetch(`${API_BASE}/admin/questions/math/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      await fetchQuestions();
    } catch (err) { setError(err.message); }
  };

  // ✅ Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredQuestions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontFamily: 'inherit' }}>Math Questions</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {error && <div style={{ padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>{error}</div>}

      <button onClick={() => { setShowAddForm(true); setEditingId(null); setFormData(getEmptyForm()); }} style={btnPrimary}>+ Add New Question</button>

      <div style={{ marginTop: 15, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: 15, background: '#fff', borderRadius: 8, border: '1px solid #ddd' }}>
        <label style={{ fontWeight: 600, fontSize: 14, width: '100%', marginBottom: 5 }}>🔍 Filters:</label>
        
        <select style={{ ...inputStyle, width: 'auto', marginBottom: 0, fontSize: 13, padding: '6px 10px' }} value={levelFilter} onChange={handleLevelChange}>
          <option value="">All Levels</option>
          {Array.from({ length: 12 }, (_, i) => (i + 1).toString()).map(l => <option key={l} value={l}>Level {l}</option>)}
        </select>

        <select style={{ ...inputStyle, width: 'auto', marginBottom: 0, fontSize: 13, padding: '6px 10px' }} value={chapterFilter} onChange={handleChapterChange}>
          <option value="">All Chapters</option>
          {availableChapters.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select style={{ ...inputStyle, width: 'auto', marginBottom: 0, fontSize: 13, padding: '6px 10px' }} value={conceptFilter} onChange={(e) => setConceptFilter(e.target.value)}>
          <option value="">All Concepts</option>
          {availableConcepts.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select style={{ ...inputStyle, width: 'auto', marginBottom: 0, fontSize: 13, padding: '6px 10px' }} value={conceptTypeFilter} onChange={(e) => setConceptTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          <option value="core">Core</option>
          <option value="supporting">Supporting</option>
        </select>

        <select style={{ ...inputStyle, width: 'auto', marginBottom: 0, fontSize: 13, padding: '6px 10px' }} value={difficultyFilter} onChange={(e) => setDifficultyFilter(e.target.value)}>
          <option value="">All Difficulties</option>
          <option value="basic">Basic</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>

        {(levelFilter || chapterFilter || conceptFilter || conceptTypeFilter || difficultyFilter) && (
          <button onClick={clearAllFilters} style={btnFilter}>Clear All</button>
        )}
        
        <span style={{ fontSize: 13, color: '#666', marginLeft: 'auto' }}>
          Showing {filteredQuestions.length} of {allQuestions.length} questions
        </span>
      </div>

      {(showAddForm || editingId) && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginTop: 20 }}>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit' : 'New'} Question</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <label style={labelStyle}>Level: <input style={inputStyle} name="level" value={formData.level} onChange={handleChange} placeholder="e.g. 3" /></label>
            <label style={labelStyle}>Chapter: <input style={inputStyle} name="chapter_name" value={formData.chapter_name || formData.chapter} onChange={(e) => setFormData({...formData, chapter_name: e.target.value, chapter: e.target.value})} placeholder="e.g. Numbers to 10" /></label>
            <label style={labelStyle}>Concept: <input style={inputStyle} name="concept" value={formData.concept} onChange={handleChange} placeholder="e.g. Counting" /></label>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <label style={labelStyle}>Concept Type: 
              <select style={inputStyle} name="concept_type" value={formData.concept_type} onChange={handleChange}>
                <option value="">Select Type</option>
                <option value="core">Core</option>
                <option value="supporting">Supporting</option>
              </select>
            </label>
            <label style={labelStyle}>Difficulty: 
              <select style={inputStyle} name="difficulty" value={formData.difficulty} onChange={handleChange}>
                <option value="">Select Difficulty</option>
                <option value="basic">Basic</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </label>
          </div>
          
          <label style={labelStyle}>Question Text: <textarea style={{ ...inputStyle, minHeight: 60 }} name="question_text" value={formData.question_text} onChange={handleChange} placeholder="Use plain text: 3/4, 2 x 3, x^2, ___" /></label>
          
          <label style={labelStyle}>Question Image:</label>
          <div style={{ padding: 12, background: '#f8f9fa', borderRadius: 6, marginBottom: 15, border: '1px solid #e9ecef' }}>
            {formData.image_url ? (
              <div style={{ textAlign: 'center', marginBottom: 10 }}>
                <img src={getImageUrl(formData.image_url)} alt="Current" style={{ maxWidth: '100%', maxHeight: 150, marginBottom: 10, borderRadius: 4, border: '1px solid #ddd' }} />
                <br/>
                <button type="button" onClick={handleRemoveImage} style={{ ...btnDanger, padding: '6px 12px', fontSize: 13 }}>🗑️ Remove Image</button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#999', padding: 10, marginBottom: 10 }}>No image attached.</div>
            )}
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                {formData.image_url ? 'Replace with new image:' : 'Upload new image:'}
              </label>
              <input type="file" accept="image/*" onChange={(e) => handleEditImageUpload(e.target.files[0])} disabled={uploadingImage || !editingId} style={{ ...inputStyle, padding: 6, marginBottom: 0 }} />
              {uploadingImage && <div style={{ fontSize: 12, color: '#007BFF', marginTop: 4 }}>⏳ Uploading image...</div>}
              {!editingId && <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>💡 Save the question first, then click "Edit" to add an image.</div>}
            </div>
          </div>

          <strong style={labelStyle}>Question Tags:</strong>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
            <input style={inputStyle} name="question_tag_1" placeholder="Tag 1" value={formData.question_tag_1} onChange={handleChange} />
            <input style={inputStyle} name="question_tag_2" placeholder="Tag 2" value={formData.question_tag_2} onChange={handleChange} />
            <input style={inputStyle} name="question_tag_3" placeholder="Tag 3" value={formData.question_tag_3} onChange={handleChange} />
          </div>

          {['a', 'b', 'c', 'd'].map(opt => (
            <div key={opt} style={{ marginBottom: 12, padding: 12, background: '#f8f9fa', borderRadius: 6 }}>
              <strong>Answer {opt.toUpperCase()}:</strong>
              <input style={inputStyle} name={`answer_${opt}`} placeholder="Answer text" value={formData[`answer_${opt}`]} onChange={handleChange} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <input style={inputStyle} name={`answer_${opt}_tag1`} placeholder="Tag 1" value={formData[`answer_${opt}_tag1`]} onChange={handleChange} />
                <input style={inputStyle} name={`answer_${opt}_tag2`} placeholder="Tag 2" value={formData[`answer_${opt}_tag2`]} onChange={handleChange} />
                <input style={inputStyle} name={`answer_${opt}_tag3`} placeholder="Tag 3" value={formData[`answer_${opt}_tag3`]} onChange={handleChange} />
              </div>
            </div>
          ))}

          <label style={labelStyle}>Correct Answer:
            <select style={inputStyle} name="correct_answer" value={formData.correct_answer} onChange={handleChange}>
              <option>A</option><option>B</option><option>C</option><option>D</option>
            </select>
          </label>
          <div style={{ marginTop: 10 }}>
            <button onClick={handleSave} style={btnPrimary}>Save</button>
            <button onClick={() => { setEditingId(null); setShowAddForm(false); }} style={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        {loading && <p style={{ textAlign: 'center' }}>Loading...</p>}
        
        {!loading && filteredQuestions.length === 0 && (
          <p style={{ textAlign: 'center', padding: 20, color: '#666' }}>
            {allQuestions.length === 0 ? 'No questions found. Add one above!' : 'No questions match your current filters.'}
          </p>
        )}

        {/* ✅ RENDER ONLY CURRENT PAGE ITEMS (25 per page) */}
        {!loading && currentItems.map(q => (
          <div key={q.id} style={{ background: '#fff', padding: 16, marginBottom: 12, borderRadius: 8, border: '1px solid #ddd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <span style={{ padding: '3px 8px', background: '#28a745', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                    Lvl {q.level || '—'}
                  </span>
                  <span style={{ padding: '3px 8px', background: '#6c757d', color: '#fff', borderRadius: 10, fontSize: 11 }}>
                    Ch: {q.chapter_name || q.chapter || '—'}
                  </span>
                  <span style={{ padding: '3px 8px', background: '#e9ecef', color: '#495057', borderRadius: 10, fontSize: 11 }}>
                    📚 {q.concept || '—'}
                  </span>
                  <span style={{ 
                    padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: q.concept_type === 'core' ? '#007BFF' : '#ffc107',
                    color: q.concept_type === 'core' ? '#fff' : '#333'
                  }}>
                    {q.concept_type?.toUpperCase() || '—'}
                  </span>
                  <span style={{ 
                    padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: '#fff',
                    background: q.difficulty === 'basic' ? '#28a745' : q.difficulty === 'intermediate' ? '#fd7e14' : '#6f42c1'
                  }}>
                    {q.difficulty?.toUpperCase() || '—'}
                  </span>
                </div>
                
                <p style={{ margin: '6px 0', fontSize: 15, lineHeight: 1.6, color: '#333' }}>
                  <MathText>{q.question_text}</MathText>
                </p>

                {q.image_url && (
                  <div style={{ margin: '10px 0', padding: 8, background: '#f8f9fa', borderRadius: 6, border: '1px solid #e9ecef', textAlign: 'center' }}>
                    <img 
                      src={getImageUrl(q.image_url)} 
                      alt="Question illustration" 
                      style={{ maxWidth: '100%', maxHeight: 200, display: 'block', margin: '0 auto', borderRadius: 4 }}
                      onError={(e) => { e.target.style.display = 'none'; if(e.target.nextSibling) e.target.nextSibling.style.display = 'block'; }}
                    />
                    <div style={{ display: 'none', color: '#856404', fontSize: 12, padding: 10, background: '#fff3cd', borderRadius: 4, marginTop: 4 }}>
                      ⚠️ Image failed to load. Path: <code>{q.image_url}</code>
                    </div>
                  </div>
                )}
                
                {(q.question_tag_1 || q.question_tag_2 || q.question_tag_3) && (
                  <div style={{ fontSize: 12, color: '#6f42c1', marginBottom: 6 }}>
                    <strong>Q-Tags:</strong> {[q.question_tag_1, q.question_tag_2, q.question_tag_3].filter(t => t).join(', ')}
                  </div>
                )}
                
                <div style={{ fontSize: 13, color: '#666', lineHeight: 2 }}>
                  {['a', 'b', 'c', 'd'].map(opt => (
                    <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <strong style={{ minWidth: 20, color: '#333' }}>{opt.toUpperCase()}:</strong>
                      <span style={{ flex: 1 }}><MathText>{q[`answer_${opt}`]}</MathText></span>
                      <span style={{ color: '#999', fontSize: 11 }}>
                        ({[q[`answer_${opt}_tag1`], q[`answer_${opt}_tag2`], q[`answer_${opt}_tag3`]].filter(t => t).join(', ')})
                      </span>
                    </div>
                  ))}
                </div>
                <span style={{ display: 'inline-block', marginTop: 6, padding: '2px 8px', background: '#28a745', color: '#fff', borderRadius: 4, fontSize: 12 }}>Correct: {q.correct_answer}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button onClick={() => { setEditingId(q.id); setShowAddForm(false); setFormData({ ...q, chapter_name: q.chapter_name || q.chapter }); }} style={{ ...btnPrimary, padding: '6px 10px', fontSize: 13 }}>Edit</button>
                <button onClick={() => handleDelete(q.id)} style={{ ...btnDanger, padding: '6px 10px', fontSize: 13 }}>Delete</button>
              </div>
            </div>
          </div>
        ))}

        {/* ✅ PAGINATION CONTROLS */}
        {!loading && totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 15, marginTop: 20, padding: 20, background: '#fff', borderRadius: 8, border: '1px solid #ddd' }}>
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
              disabled={currentPage === 1}
              style={{ ...btnSecondary, opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              ← Previous
            </button>
            <span style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
              disabled={currentPage === totalPages}
              style={{ ...btnPrimary, opacity: currentPage === totalPages ? 0.5 : 1, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}